import { GRADES, MASTERY_DEFINITIONS } from "../../shared/types";
import type { Child, Entry, Grade, Material, Program } from "../../shared/types";
import { clockTime } from "./dom";

/** Unchanged from the prototype, so a family's existing record opens as it is. */
export const STORAGE_KEY = "ll.v1";

/** The child shape the prototype stored. The shared `Child` is built from it only when needed. */
export interface LedgerChild {
  id: string;
  name: string;
  grade: Grade | "";
}

export interface LedgerSettings {
  mastery: string;
  from: string;
  to: string;
  parentName: string;
}

export interface LedgerState {
  children: LedgerChild[];
  active: string;
  entries: Entry[];
  materials: Material[];
  settings: LedgerSettings;
}

export interface EntryDraft {
  text: string;
  subjects?: readonly string[];
  minutes?: number;
  photo?: string | null;
  stuck?: boolean;
  askGuide?: boolean;
}

export interface LedgerStats {
  entries: number;
  days: number;
  hours: string;
  hoursPerWeek: string;
  subjects: number;
}

const DEFAULT_CHILD_NAME = "My child";
const DEFAULT_GRADE: Grade = "Grade 4";
const SCHOOL_DAYS_PER_WEEK = 5;

let issued = 0;

/** Same form as the prototype's ids, with a counter so ids made in the same millisecond differ. */
function uid(now: number): string {
  issued += 1;
  return now.toString(36) + issued.toString(36) + Math.random().toString(36).slice(2, 7);
}

function defaultSettings(now: number): LedgerSettings {
  const year = new Date(now).getFullYear();
  return { mastery: "", from: `${year}-08-01`, to: `${year + 1}-06-30`, parentName: "" };
}

export function defaults(now: number): LedgerState {
  const child: LedgerChild = { id: uid(now), name: DEFAULT_CHILD_NAME, grade: DEFAULT_GRADE };
  return { children: [child], active: child.id, entries: [], materials: [], settings: defaultSettings(now) };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function isGrade(value: unknown): value is Grade {
  return GRADES.some((grade) => grade === value);
}

function readChild(raw: unknown): LedgerChild | null {
  if (!isRecord(raw) || typeof raw.id !== "string") return null;
  return { id: raw.id, name: text(raw.name) || DEFAULT_CHILD_NAME, grade: isGrade(raw.grade) ? raw.grade : "" };
}

function readEntry(raw: unknown): Entry | null {
  if (!isRecord(raw)) return null;
  if (typeof raw.id !== "string" || typeof raw.childId !== "string" || typeof raw.ts !== "number") return null;
  const entry: Entry = {
    id: raw.id,
    childId: raw.childId,
    ts: raw.ts,
    text: text(raw.text),
    subjects: list(raw.subjects).filter((s): s is string => typeof s === "string"),
    minutes: typeof raw.minutes === "number" && Number.isFinite(raw.minutes) ? raw.minutes : 0,
  };
  if (typeof raw.photo === "string" && raw.photo !== "") entry.photo = raw.photo;
  if (raw.stuck === true) entry.stuck = true;
  if (raw.askGuide === true) entry.askGuide = true;
  if (raw.example === true) entry.example = true;
  return entry;
}

function readMaterial(raw: unknown): Material | null {
  if (!isRecord(raw)) return null;
  return { title: text(raw.title), author: text(raw.author), publisher: text(raw.publisher), year: text(raw.year) };
}

function readSettings(raw: unknown, now: number): LedgerSettings {
  if (!isRecord(raw)) return defaultSettings(now);
  return { mastery: text(raw.mastery), from: text(raw.from), to: text(raw.to), parentName: text(raw.parentName) };
}

function readAll<T>(value: unknown, read: (raw: unknown) => T | null): T[] {
  return list(value).flatMap((raw) => {
    const item = read(raw);
    return item === null ? [] : [item];
  });
}

function parseJson(raw: string | null): unknown {
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Tolerant loader. Anything missing is filled in the way the prototype's load() did,
 * and a record that cannot be read at all opens as a fresh one.
 */
export function parseState(raw: string | null, now: number): LedgerState {
  const stored = parseJson(raw);
  if (!isRecord(stored)) return defaults(now);

  const fresh = defaults(now);
  const storedChildren = readAll(stored.children, readChild);
  const children = storedChildren.length > 0 ? storedChildren : fresh.children;
  const first = children[0];
  const active = children.find((c) => c.id === stored.active)?.id ?? first?.id ?? fresh.active;

  return {
    children,
    active,
    entries: readAll(stored.entries, readEntry),
    materials: readAll(stored.materials, readMaterial),
    settings: readSettings(stored.settings, now),
  };
}

export function loadState(storage: Pick<Storage, "getItem">, now: number): LedgerState {
  try {
    return parseState(storage.getItem(STORAGE_KEY), now);
  } catch {
    return defaults(now);
  }
}

/** Returns false when the browser refuses the write, which in practice means it is out of room. */
export function saveState(storage: Pick<Storage, "setItem">, state: LedgerState): boolean {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

export function eraseState(storage: Pick<Storage, "removeItem">): boolean {
  try {
    storage.removeItem(STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

export function activeChild(state: LedgerState): LedgerChild {
  const found = state.children.find((c) => c.id === state.active) ?? state.children[0];
  return found ?? { id: state.active, name: DEFAULT_CHILD_NAME, grade: DEFAULT_GRADE };
}

export function setActive(state: LedgerState, childId: string): LedgerState {
  return state.children.some((c) => c.id === childId) ? { ...state, active: childId } : state;
}

export function updateSettings(state: LedgerState, patch: Partial<LedgerSettings>): LedgerState {
  return { ...state, settings: { ...state.settings, ...patch } };
}

/** Entries for one child, newest first, examples included. This is what the Log tab shows. */
export function entriesFor(state: LedgerState, childId: string): Entry[] {
  return state.entries.filter((e) => e.childId === childId).sort((a, b) => b.ts - a.ts);
}

function wholeMinutes(minutes: number | undefined): number {
  return minutes !== undefined && Number.isFinite(minutes) && minutes > 0 ? Math.floor(minutes) : 0;
}

/** Every field is optional. An entry needs a line of text or a photo, and nothing else. */
export function addEntry(state: LedgerState, draft: EntryDraft, now: number): LedgerState {
  const trimmed = draft.text.trim();
  if (trimmed === "" && !draft.photo) return state;
  const entry: Entry = {
    id: uid(now),
    childId: state.active,
    ts: now,
    text: trimmed,
    subjects: [...(draft.subjects ?? [])],
    minutes: wholeMinutes(draft.minutes),
  };
  if (draft.photo) entry.photo = draft.photo;
  if (draft.stuck === true) entry.stuck = true;
  if (draft.askGuide === true) entry.askGuide = true;
  return { ...state, entries: [...state.entries, entry] };
}

export function deleteEntry(state: LedgerState, entryId: string): LedgerState {
  return { ...state, entries: state.entries.filter((e) => e.id !== entryId) };
}

export function addChild(state: LedgerState, name: string, grade: Grade, now: number): LedgerState {
  const trimmed = name.trim();
  if (trimmed === "") return state;
  return { ...state, children: [...state.children, { id: uid(now), name: trimmed, grade }] };
}

/** Removes the child and that child's entries. The last child stays, so the ledger always has one. */
export function removeChild(state: LedgerState, childId: string): LedgerState {
  if (state.children.length < 2) return state;
  const children = state.children.filter((c) => c.id !== childId);
  const first = children[0];
  if (!first || children.length === state.children.length) return state;
  return {
    ...state,
    children,
    entries: state.entries.filter((e) => e.childId !== childId),
    active: state.active === childId ? first.id : state.active,
  };
}

export function addMaterial(state: LedgerState, material: Material): LedgerState {
  const trimmed: Material = {
    title: material.title.trim(),
    author: material.author.trim(),
    publisher: material.publisher.trim(),
    year: material.year.trim(),
  };
  if (trimmed.title === "" && trimmed.author === "") return state;
  return { ...state, materials: [...state.materials, trimmed] };
}

export function removeMaterial(state: LedgerState, index: number): LedgerState {
  return { ...state, materials: state.materials.filter((_, i) => i !== index) };
}

export function toChild(child: LedgerChild): Child {
  return {
    id: child.id,
    firstName: child.name,
    legalName: child.name,
    birthdate: "",
    grade: child.grade,
    interests: "",
  };
}

export function toProgram(state: LedgerState): Program {
  const { mastery, from, to } = state.settings;
  return { mastery, from, to, materials: state.materials };
}

export function ledgerStats(entries: readonly Entry[]): LedgerStats {
  const minutes = entries.reduce((sum, e) => sum + e.minutes, 0);
  const days = new Set(entries.map((e) => new Date(e.ts).toDateString())).size;
  const weeks = Math.max(1, days / SCHOOL_DAYS_PER_WEEK);
  return {
    entries: entries.length,
    days,
    hours: minutes > 0 ? (minutes / 60).toFixed(1) : "0",
    hoursPerWeek: minutes > 0 ? (minutes / 60 / weeks).toFixed(1) : "0",
    subjects: new Set(entries.flatMap((e) => e.subjects)).size,
  };
}

/** yyyy-mm-dd in the device's time zone, so the date agrees with the time printed next to it. */
export function localIsoDate(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const CSV_HEADER = ["date", "time", "child", "subjects", "minutes", "note", "has_photo", "stuck", "ask_guide"];

function yesNo(value: boolean | undefined): string {
  return value === true ? "yes" : "no";
}

function csvRow(cells: readonly string[]): string {
  return cells.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",");
}

export function toCsv(state: LedgerState): string {
  const names = new Map(state.children.map((c) => [c.id, c.name]));
  const rows = [...state.entries]
    .sort((a, b) => a.ts - b.ts)
    .map((e) => [
      localIsoDate(e.ts),
      clockTime(e.ts),
      names.get(e.childId) ?? "",
      e.subjects.join("; "),
      String(e.minutes),
      e.text,
      yesNo(e.photo !== undefined),
      yesNo(e.stuck),
      yesNo(e.askGuide),
    ]);
  return [CSV_HEADER, ...rows].map(csvRow).join("\r\n");
}

/** Optional starting text for the Chapter 12 "method of determining mastery" field. */
export function masteryWording(): string {
  const { exploring, practicing, fluent } = MASTERY_DEFINITIONS;
  return ["We look at each skill in three stages.", exploring, practicing, fluent].join(" ");
}

interface ExampleEntry {
  daysAgo: number;
  hour: number;
  minute: number;
  text: string;
  subjects: string[];
  minutes: number;
}

const EXAMPLES: readonly ExampleEntry[] = [
  {
    daysAgo: 0,
    hour: 9,
    minute: 10,
    text: "Read two chapters of Hatchet out loud. Argued for ten minutes about whether the pilot should have taught him more before the heart attack.",
    subjects: ["Language arts"],
    minutes: 45,
  },
  {
    daysAgo: 0,
    hour: 10,
    minute: 30,
    text: "Long division with remainders. Got stuck on the second one, walked away, came back and got it.",
    subjects: ["Math"],
    minutes: 30,
  },
  {
    daysAgo: 0,
    hour: 14,
    minute: 0,
    text: "Tide pools at Lanikai. Counted five kinds of crab and drew three of them in the nature journal.",
    subjects: ["Science", "Art"],
    minutes: 90,
  },
  {
    daysAgo: 1,
    hour: 8,
    minute: 45,
    text: "Handwriting practice, then wrote a postcard to Grandma in Hilo.",
    subjects: ["Language arts"],
    minutes: 25,
  },
  {
    daysAgo: 1,
    hour: 11,
    minute: 15,
    text: "Watched the Hawaiian monarchy segment, then found Iolani Palace on the map and traced the timeline.",
    subjects: ["Social studies"],
    minutes: 40,
  },
  { daysAgo: 1, hour: 15, minute: 30, text: "Soccer at the park with the co-op kids.", subjects: ["PE"], minutes: 60 },
  {
    daysAgo: 2,
    hour: 9,
    minute: 0,
    text: "Ukulele, C and F chords. Fifteen minutes before it fell apart, which is longer than last week.",
    subjects: ["Music"],
    minutes: 15,
  },
  {
    daysAgo: 2,
    hour: 9,
    minute: 30,
    text: "Measured the garden bed and worked out how many plants fit at eight inches apart. Real multiplication.",
    subjects: ["Math", "Science"],
    minutes: 35,
  },
  {
    daysAgo: 2,
    hour: 13,
    minute: 0,
    text: "Read independently, forty minutes, no prompting.",
    subjects: ["Language arts"],
    minutes: 40,
  },
];

const EXAMPLE_MASTERY =
  "Daily observation and conversation, with a short oral or written check at the end of each unit. Work samples and a nature journal are kept for each subject.";

const EXAMPLE_MATERIALS: readonly Material[] = [
  { title: "Hatchet", author: "Paulsen, Gary", publisher: "Bradbury Press", year: "1987" },
  { title: "Math Mammoth Grade 4", author: "Miller, Maria", publisher: "Math Mammoth", year: "2021" },
];

/** Three sample days for the active child. Every seeded entry is marked `example: true`. */
export function seedExamples(state: LedgerState, now: number): LedgerState {
  const today = new Date(now);
  const seeded = EXAMPLES.map((example): Entry => {
    const at = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - example.daysAgo,
      example.hour,
      example.minute,
      0,
      0,
    );
    return {
      id: uid(now),
      childId: state.active,
      ts: at.getTime(),
      text: example.text,
      subjects: [...example.subjects],
      minutes: example.minutes,
      example: true,
    };
  });
  return {
    ...state,
    entries: [...state.entries, ...seeded],
    materials: state.materials.length > 0 ? state.materials : EXAMPLE_MATERIALS.map((m) => ({ ...m })),
    settings: state.settings.mastery ? state.settings : { ...state.settings, mastery: EXAMPLE_MASTERY },
  };
}
